import { useMemo, useRef, useState } from "react";
import { PencilRulerIcon, Trash2Icon } from "lucide-react";

import { useCameras } from "@/api/cameras";
import {
  useCreateZone,
  useDeleteZone,
  useUpdateZone,
  useZones,
} from "@/api/sinsight";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type Pt = [number, number];

/**
 * S16-batch — draw a POLYGON (zone_intrusion) or LINE (line_crossing)
 * over a camera view. Coordinates are normalised 0-1 so the shape
 * survives any resolution. No snapshot wired here yet — draw on the
 * grid; the normalised geometry is what matters.
 */
export default function ZonesPage() {
  const { data: cameras } = useCameras({ limit: 200 });
  const [cameraId, setCameraId] = useState<string>("");
  const { data: zones } = useZones(cameraId ? { camera_id: cameraId } : undefined);
  const create = useCreateZone();
  const update = useUpdateZone();
  const del = useDeleteZone();

  const [shapeType, setShapeType] = useState<"polygon" | "line">("polygon");
  const [name, setName] = useState("Zone 1");
  const [pts, setPts] = useState<Pt[]>([]);
  const [triggerSide, setTriggerSide] = useState<"left" | "right">("left");
  const svgRef = useRef<SVGSVGElement>(null);

  const maxPts = shapeType === "line" ? 2 : 999;

  const onSvgClick = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = svgRef.current!.getBoundingClientRect();
    const x = +((e.clientX - rect.left) / rect.width).toFixed(4);
    const y = +((e.clientY - rect.top) / rect.height).toFixed(4);
    setPts((p) => (p.length >= maxPts ? p : [...p, [x, y]]));
  };

  const canSave =
    Boolean(cameraId) &&
    ((shapeType === "polygon" && pts.length >= 3) ||
      (shapeType === "line" && pts.length === 2));

  const onSave = async () => {
    if (!canSave) return;
    const geometry =
      shapeType === "polygon"
        ? { vertices: pts }
        : { a: pts[0], b: pts[1], trigger_side: triggerSide };
    await create.mutateAsync({
      name,
      shape_type: shapeType,
      geometry,
      camera_id: cameraId,
    });
    setPts([]);
  };

  const polyPoints = useMemo(
    () => pts.map(([x, y]) => `${x * 100},${y * 100}`).join(" "),
    [pts],
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <PencilRulerIcon className="h-6 w-6" />
          Analytics — Zones &amp; Lines
        </h1>
        <p className="text-sm text-muted-foreground">
          Draw a <strong>polygon</strong> region (intrusion) or a{" "}
          <strong>line</strong> tripwire over a camera. Coordinates are
          stored normalised (0–1) so shapes survive resolution changes.
          Line crossing is a stateless "wrong side of the line" check —
          true directional crossing arrives with the tracking sprint.
        </p>
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="grid gap-3 md:grid-cols-3">
            <div className="space-y-1">
              <Label>Camera</Label>
              <select
                className="h-9 w-full rounded border bg-background px-2 text-sm"
                value={cameraId}
                onChange={(e) => { setCameraId(e.target.value); setPts([]); }}
              >
                <option value="">— select camera —</option>
                {(cameras?.items ?? []).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label>Shape</Label>
              <select
                className="h-9 w-full rounded border bg-background px-2 text-sm"
                value={shapeType}
                onChange={(e) => {
                  setShapeType(e.target.value as "polygon" | "line");
                  setPts([]);
                }}
              >
                <option value="polygon">Polygon (zone intrusion)</option>
                <option value="line">Line (line crossing)</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
          </div>

          {shapeType === "line" && (
            <div className="space-y-1">
              <Label>Trigger side (which side fires)</Label>
              <select
                className="h-9 w-48 rounded border bg-background px-2 text-sm"
                value={triggerSide}
                onChange={(e) => setTriggerSide(e.target.value as "left" | "right")}
              >
                <option value="left">left of A→B</option>
                <option value="right">right of A→B</option>
              </select>
            </div>
          )}

          <svg
            ref={svgRef}
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            onClick={onSvgClick}
            className="h-72 w-full cursor-crosshair rounded border bg-[#263238]"
          >
            {/* grid */}
            {[25, 50, 75].map((g) => (
              <g key={g} stroke="#37474f" strokeWidth="0.3">
                <line x1={g} y1="0" x2={g} y2="100" />
                <line x1="0" y1={g} x2="100" y2={g} />
              </g>
            ))}
            {shapeType === "polygon" && pts.length >= 2 && (
              <polygon points={polyPoints} fill="#ff6d0033" stroke="#ff6d00"
                       strokeWidth="0.6" />
            )}
            {shapeType === "line" && pts.length === 2 && (
              <line x1={pts[0][0] * 100} y1={pts[0][1] * 100}
                    x2={pts[1][0] * 100} y2={pts[1][1] * 100}
                    stroke="#ff6d00" strokeWidth="0.8" />
            )}
            {pts.map(([x, y], i) => (
              <circle key={i} cx={x * 100} cy={y * 100} r="1.2" fill="#ff6d00" />
            ))}
          </svg>

          <div className="flex items-center gap-2">
            <Button onClick={onSave} disabled={!canSave || create.isPending}>
              Save shape
            </Button>
            <Button variant="outline" onClick={() => setPts([])}>Clear</Button>
            <span className="text-xs text-muted-foreground">
              {shapeType === "polygon"
                ? `${pts.length} vertices (need ≥3)`
                : `${pts.length}/2 points (A then B)`}
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <h2 className="mb-2 text-lg font-semibold">Shapes on this camera</h2>
          {!cameraId && (
            <p className="text-xs text-muted-foreground">Select a camera.</p>
          )}
          <ul className="space-y-2">
            {(zones?.items ?? []).map((z) => (
              <li key={z.id}
                  className="flex items-center justify-between rounded border p-2 text-sm">
                <div>
                  <span className="font-medium">{z.name}</span>{" "}
                  <Badge variant="secondary">{z.shape_type}</Badge>{" "}
                  <Badge variant={z.is_active ? "online" : "secondary"}>
                    {z.is_active ? "active" : "inactive"}
                  </Badge>
                  {z.subject_id && (
                    <span className="ml-1 font-mono text-xs opacity-60">
                      subj {z.subject_id.slice(0, 8)}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm"
                    onClick={() => update.mutate({ id: z.id, patch: { is_active: !z.is_active } })}>
                    {z.is_active ? "Disable" : "Enable"}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => del.mutate(z.id)}>
                    <Trash2Icon className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
